/*!
  Pioneer WFRIA ActivityMonitor v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["ActivityMonitor"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("ActivityMonitor") < 0) {
    window.__PIONEER_GLOBAL__.components.push("ActivityMonitor");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["customStrings", "pingUrl", "waitTimeToPingServerOnActivity", "waitTimetoDisplayWarningBanner", "warningBannerDuration", "onWarning", "domain", "inactive", "onInactive", "zIndex", "type", "signOffUrl", "cookiePrefix"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import Button from "../Button/Button";
import { EVENT_TYPES, KEYBOARD_KEYS, ZINDEX } from "../common/constants";
import { useUniqueId } from "../common/hooks";
import { requiredIf } from "../common/proptypes";
import MessageBar from "../MessageBar/MessageBar";
import ActivityMonitorUtilities from "./ActivityMonitorUtilities";
import extractTheme from "./extractTheme";
import STRINGS from "./strings";
import { IconWarning } from "..";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const DEFAULT_POLL_INTERVAL = 3000;
const ACTIVITY_BANNER_POLL_INTERVAL = 500;
const FLAG_KILL_ACTIVITY = "k";
const FLAG_ACTIVITY_TIMESTAMP = "t";
const FLAG_LOGON_URL = "u";
const LOGON = "logon";
const ROOT = "root";
const CHILD = "child";
const MFE = "mfe";
const MISSING_ROOT = "missingRoot";
const setCookieData = ActivityMonitorUtilities.setCookieData;
const getCookieData = ActivityMonitorUtilities.getCookieData;
/**
 * Gets the current time.
 */

const getCurrentTime = () => {
  return new Date().getTime();
};
/**
 * ActivityMonitor tracks user activity across pages in the same domain,
 * and warns the user before they become inactive. The user can interact with the message
 * or the page to continue the activity.
 */


const ActivityMonitor = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    customStrings,
    pingUrl,
    waitTimeToPingServerOnActivity,
    waitTimetoDisplayWarningBanner,
    warningBannerDuration,
    onWarning,
    domain,
    inactive,
    onInactive,
    zIndex,
    type,
    signOffUrl,
    cookiePrefix
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();
  const {
    cssRoot,
    cssMask,
    cssMessageContent,
    cssMessagebarStyles
  } = extractTheme(_objectSpread({
    theme
  }, otherProps));
  const events = [EVENT_TYPES.KEYUP, EVENT_TYPES.CLICK, EVENT_TYPES.SCROLL];
  const messageContainerRef = useRef();
  const continueActivityID = useUniqueId({
    prefix: "pnr-activity-monitor__message-container-"
  });
  const bannerContainer = useRef();
  const activeElementPriorToBannerDisplay = useRef();
  const pollIntervalId = useRef();
  const lastPing = useRef();
  const activityPingServerTimeout = useRef();
  const warningBannerTimeout = useRef();
  const inactiveTimeout = useRef();

  const componentStrings = _objectSpread(_objectSpread({}, STRINGS), customStrings);

  const currentTime = getCurrentTime();
  const [activity, setActivity] = useState(currentTime);
  const activityPrevValue = useRef(activity);
  const [showWarningBanner, setShowWarningBanner] = useState(false);
  const nonMfeType = type !== MFE;
  /**
   * Clears `activityPingServerTimeout`, `warningBannerTimeout` and `inactiveTimeout`.
   */

  const clearTimeouts = () => {
    clearTimeout(activityPingServerTimeout.current);
    clearTimeout(warningBannerTimeout.current);
    clearTimeout(inactiveTimeout.current);
  };
  /**
   * Returns a string containing the pingUrl value.
   */


  const getPingUrl = () => {
    if (typeof pingUrl === "string") {
      return pingUrl;
    } else {
      return pingUrl && pingUrl();
    }
  };
  /**
   * Pings server resource in order to keep the application activity alive.
   * This is done by setting the source of an image to the `pingUrl`.
   *
   * @param {Number} timeStamp A date time corresponding to the time of the activity.
   */


  const pingServer = timeStamp => {
    const image = new Image();
    timeStamp = timeStamp || getCurrentTime();
    lastPing.current = timeStamp;
    image.src = getPingUrl();
  };
  /**
   * Sets the timer after which the warning banner should appear. By default,
   * display the warning banner 15 minutes after no activity is detected.
   */


  const setTimerToDisplayWarningBanner = () => {
    /**
     * Once the warning banner is displayed, set polling to occur every 500ms,
     * thereby increasing activity detection on related windows. This is
     * important as 1 minute remains before "inactivity".
     */
    return setTimeout(() => {
      /**
       * Storing the active element which needs to be focused back on dismiss
       * of the warning banner.
       */
      activeElementPriorToBannerDisplay.current = document.activeElement;
      /**
       * Set `showWarningBanner` state to `true` to display warning banner and
       * trigger `onWarning()` callback handler.
       */

      setShowWarningBanner(true);
    }, waitTimetoDisplayWarningBanner);
  };
  /**
   * Sets the timer after which to ping the server to keep the activity alive.
   * This is called whenever a new activity is detected on the page.
   * Ping the server 30 seconds(default timeout) after the latest activity.
   */


  const setTimerOnActivityToPingServer = () => {
    return setTimeout(() => {
      // Clear existing timeouts.
      clearTimeouts(); // Ping the server to keep the application activity alive.

      pingServer();
      /**
       * Start the timer to display the warning banner by default after
       * 13 minutes 30 seconds. Clear this timer if activity is detected.
       */

      warningBannerTimeout.current = setTimerToDisplayWarningBanner();
    }, waitTimeToPingServerOnActivity);
  };
  /**
   * Stops polling. The window will no longer be alerted of activity which occurs
   * outside itself via the WRIA cookie.
   */


  const stopWriaCookieDataPolling = () => {
    if (pollIntervalId.current) {
      clearInterval(pollIntervalId.current);
    }
  };
  /**
   * Clears all the timeouts set, stops polling of the cookie data, and sets the
   * value of the FLAG_KILL_ACTIVITY cookie to `true`. This is done so that the
   * other windows polling the cookie data get the latest FLAG_KILL_ACTIVITY
   * value and would know to terminate ActivityMonitor.
   */


  const terminate = () => {
    clearTimeouts();
    stopWriaCookieDataPolling(); // Set the FLAG_KILL_ACTIVITY cookie flag to true, when the activity is terminated.

    setCookieData(cookiePrefix, FLAG_KILL_ACTIVITY, true, domain);
    const logonUrlCookieValue = getCookieData(cookiePrefix, FLAG_LOGON_URL);

    if (!signOffUrl && onInactive) {
      /*
       * If the `signOffUrl` is not configured in timeout scenarios,
       * execute the `onInactive` callback if configured.
       */
      onInactive();
    } else if (type === ROOT && signOffUrl) {
      /**
       * Redirect to the signOffUrl when either the root window's session is expired or
       * when the Sign Off link is clicked.
       */
      window.location = signOffUrl;
    } else if (type === CHILD && logonUrlCookieValue === MISSING_ROOT && signOffUrl) {
      /**
       * Empty the logon url cookie value so that any other opened child windows are closed instead
       * of redirecting to `signOffUrl`
       */
      setCookieData(cookiePrefix, FLAG_LOGON_URL, "", domain);
      /*
       * If Root window is closed. And, child window's session is terminated,
       * then redirect the child window to the `signOffUrl`.
       */

      window.location = signOffUrl;
    } else if (type === CHILD) {
      /*
       * If Root window is redirected to signOffUrl, then focus the child
       * window and close it only when `onInactive` prop is not configured.
       */
      window.focus();
      window.close();
    }
  };
  /**
   * Sets the timer after which the state will become inactive. By default the
   * state will be inactive after 1 minute post the warning banner is displayed.
   */


  const setTimerToInactive = () => {
    // Call the terminate method once the `warningBannerDuration` has been passed.
    return setTimeout(() => {
      terminate();
    }, warningBannerDuration);
  };
  /**
   * Pings the server in order to keep the application activity alive,
   * only if there has been continuous activity for more than 30 seconds.
   */


  const pingServerOnActivity = activity => {
    /**
     * If current activity on the page minus the last time the activity on the
     * page was detected is greater than 30 seconds that means there has been
     * continuous activity for more than 30 seconds. In this case ping the server.
     * This enables us to maintain the application activity alive since the ping
     * to the the server gets triggered only after 30 seconds past the last
     * activity on the window.
     */
    if (activity - lastPing.current > waitTimeToPingServerOnActivity) {
      /**
       * We haven't pinged in a while even though there is activity. Ping the server.
       */
      pingServer(activity);
    }
  };
  /**
   * This is the core method which keeps the current window aware and in sync
   * with other windows with the same domain via reading from the WRIA_JAR cookie.
   * It reads the cookie data and determines whether there is any activity detected
   * in all the other open windows of same domain. If there is an activity detected,
   * then the current window updates its own activity to match with the latest activity
   * stored in the cookie. Once the current window updates its activity stored in the
   * state, the timer to wait to ping the server to keep the application activity alive.
   */


  const syncCookieData = () => {
    const data = getCookieData(cookiePrefix);
    const activityStoredInCookie = data[FLAG_ACTIVITY_TIMESTAMP] || 0;
    const isKill = data[FLAG_KILL_ACTIVITY] || false;
    const currentWindowActivity = activity;

    if (isKill && nonMfeType) {
      /**
       * type `mfe` window only polls for activity and pings the server url
       * as needed. If type is `mfe`, it means that the application is rendered
       * in another container application which will have type as either `root` or `child`.
       * So terminating `root` or `child` application will be enough.
       * termination is not needed for type `mfe`.
       */
      terminate();
    } else if (currentWindowActivity < activityStoredInCookie) {
      /**
       * If the current window activity is less than the activity that happened on other
       * open windows then reset the current window's activity and check whether there
       * has been continuous activity or not in order to ping the server by
       * calling (pingServerOnActivity).
       */
      pingServerOnActivity(activityStoredInCookie);
      setActivity(activityStoredInCookie);
      /**
       * warning banner should not be shown for type `mfe`.
       */

      nonMfeType && setShowWarningBanner(false);
    }
  };
  /**
   * Start Polling to get the existing cookie data and sync it with the current
   * window's activity.
   */


  const restartWriaCookieDataPolling = () => {
    // Stop the existing polling to read/write to the WRIA specific cookie data.
    stopWriaCookieDataPolling();
    /**
     * Check if warning banner is displayed or not. If it is displayed change the
     * time at which the polling occurs from `3s` to `500ms`. This update is done
     * in order to detect any activity on other open windows much faster since there
     * would only be 1 minute of time left before the state become inactive.
     */

    const pollInterval = showWarningBanner ? ACTIVITY_BANNER_POLL_INTERVAL : DEFAULT_POLL_INTERVAL;
    pollIntervalId.current = setInterval(syncCookieData, pollInterval);
  };
  /**
   * Handler for events that are considered "activities" such as click and scroll.
   */


  const handleDocumentEvent = e => {
    var _activeElementPriorTo;

    const {
      type,
      key
    } = e;

    if (showWarningBanner && (type === EVENT_TYPES.SCROLL || key === KEYBOARD_KEYS.SHIFT || key === KEYBOARD_KEYS.TAB)) {
      /**
       * Since `scroll`, `tab` and `shift` should not trigger any activity on the =
       * window when the warning banner is displayed, exit this method.
       */
      return;
    } // Clear any existing timeouts.


    clearTimeouts();
    const currentTime = getCurrentTime();
    /**
     * Set the current window activity timestamp in the cookie, so that other
     * windows implementing ActivityMonitor would poll for the cookie data
     * and keep the window activity alive.
     */

    setCookieData(cookiePrefix, FLAG_ACTIVITY_TIMESTAMP, currentTime, domain);
    /**
     * Call this method to check for any continuous activity on the page for more
     * than 30 seconds and ping the server to keep the application activity alive.
     */

    pingServerOnActivity(currentTime); // Restart the polling after an activity has been registered.

    setShowWarningBanner(false);
    setActivity(currentTime);
    restartWriaCookieDataPolling();
    (_activeElementPriorTo = activeElementPriorToBannerDisplay.current) === null || _activeElementPriorTo === void 0 ? void 0 : _activeElementPriorTo.focus();
    activeElementPriorToBannerDisplay.current = null;
  };
  /**
   * Adds/removes event listeners.
   *
   * @param {Boolean} Set to `true` in order to add event listeners.
   */


  const toggleActivityEventListeners = add => {
    const eventsLength = events.length;

    for (let i = 0; i < eventsLength; i++) {
      if (add) {
        document.addEventListener(events[i], handleDocumentEvent);
      } else {
        document.removeEventListener(events[i], handleDocumentEvent);
      }
    }
  };
  /**
   * Handler called on unload of the window.
   */


  const handleWindowUnload = () => {
    if (type === ROOT) {
      /**
       * `missingRoot` should be set as the value of the FLAG_LOGON_URL
       * when the window of type `root` closes.
       * This is done in order for child window to redirect to the logon
       * page instead of closing
       * when the session expires.
       */
      setCookieData(cookiePrefix, FLAG_LOGON_URL, MISSING_ROOT, domain);
    } else {
      // Reset the activity.
      ActivityMonitor.resetTrackingActivity(domain, type, cookiePrefix);
    }
  };
  /**
   * Adds/removes event listeners.
   *
   * @param {Boolean} Set to `true` in order to add event listeners.
   */


  const toggleUnloadEventListener = add => {
    if (add) {
      window.addEventListener(EVENT_TYPES.UNLOAD, handleWindowUnload);
    } else {
      window.removeEventListener(EVENT_TYPES.UNLOAD, handleWindowUnload);
    }
  };
  /**
   * Handler called on click of Continue button within the warning banner.
   */


  const handleContinueActivityClick = () => {
    setShowWarningBanner(false);
    setActivity(getCurrentTime());
  };

  useEffect(() => {
    if (type === LOGON || type === MFE) {
      return;
    }

    if (inactive) {
      // If the inactive flag is set to `true`, then terminate activity.
      terminate();
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [inactive]);
  useEffect(() => {
    if (type === MFE) {
      /**
       * The method is called in order to start polling every 3 seconds
       * to get the existing cookie data and sync it with the current
       * window's activity.
       */
      restartWriaCookieDataPolling();
      /**
       * This property records the time when the ActivityMonitor is initially
       * loaded. We use this property to ping the server if the page has been
       * interacted with continuously for 30 seconds.
       */

      lastPing.current = activity;
      /**
       * Set the activity timestamp cookie to the time when the ActivityMonitor
       * is initially loaded.
       */

      setCookieData(cookiePrefix, FLAG_ACTIVITY_TIMESTAMP, lastPing.current, domain);
      return () => {
        /** Clear existing timeouts. */
        clearTimeouts();
        /**
         * Stop the polling that takes place every 3 seconds to read the
         * WRIA specific cookie.
         */

        stopWriaCookieDataPolling();
      };
    }

    if (type !== CHILD) {
      /*
       * This method adds window unload event listener for both
       * window types LOGON and ROOT.
       * The unload event listener is added for LOGON in order to
       * clear the WRIA specific cookie data.
       * The unload event listener is added for window type ROOT in order to set the `missingRoot`
       * as the value to the flag `u` in the WRIA specific cookie.
       */
      toggleUnloadEventListener(true);

      if (type === LOGON) {
        /**
         * Since Logon page doesn't require to track activity, there is no need of adding event listeners
         * on the document nor
         * polling for reading the WRIA specific cookie data or
         * set any WRIA specific cookie data.
         */
        return;
      }
    }
    /**
     * The method is called in order to start polling every 3 seconds
     * to get the existing cookie data and sync it with the current
     * window's activity.
     */


    restartWriaCookieDataPolling();
    /** Empty the logon url cookie value if it is present when the ActivityMonitor is initially loaded. */

    setCookieData(cookiePrefix, FLAG_LOGON_URL, "", domain);
    /**
     * This property records the time when the ActivityMonitor is initially
     * loaded. We use this property to ping the server if the page has been
     * interacted with continuously for 30 seconds.
     */

    lastPing.current = activity;
    /**
     * Set the activity timestamp cookie to the time when the ActivityMonitor
     * is initially loaded.
     */

    setCookieData(cookiePrefix, FLAG_ACTIVITY_TIMESTAMP, lastPing.current, domain);
    clearTimeouts();
    /**
     * Start the timer to ping the server in order to keep activity alive
     * after the activity timestamp set in the cookie.
     */

    activityPingServerTimeout.current = setTimerOnActivityToPingServer();
    return () => {
      /**
       * Empty the logon url cookie value if it is present when the SessionManager is unmounted.
       * This is to ensure that the `missingRoot` value which is set on unload of the window
       * in the FLAG_LOGON_URL is cleared when the component unmounts.
       */
      setCookieData(cookiePrefix, FLAG_LOGON_URL, "", domain);
      /** Detach window listeners. */

      toggleUnloadEventListener();
      /** Clear existing timeouts. */

      clearTimeouts();
      /**
       * Stop the polling that takes place every 3 seconds to read the
       * WRIA specific cookie.
       */

      stopWriaCookieDataPolling();
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    /**
     * When type if `MFE`, it only requires to poll for cookie data
     * and ping the server if required
     *
     */
    if (type === MFE) {
      return;
    }
    /**
     * When new activity occurs, clear the existing timeouts and start
     * the timer to ping the server in order to keep activity alive.
     */


    if (activityPrevValue.current !== activity) {
      clearTimeouts();
      activityPingServerTimeout.current = setTimerOnActivityToPingServer();
    }

    if (showWarningBanner) {
      onWarning && onWarning();
      restartWriaCookieDataPolling();
      bannerContainer.current = document.querySelector(`[aria-labelledby=${continueActivityID}]`); // Focus on the message container when the warning banner is displayed.

      const messageContainerRefNode = messageContainerRef.current;
      /**
       * Focusable Node (tabIndex=-1) will be available in messageBar
       * to access it we need to traverse to parent level and this structure won't change
       */

      messageContainerRefNode && messageContainerRefNode.parentNode.parentNode.focus(); // Once the warning banner is visible, start the timer to inactive.

      inactiveTimeout.current = setTimerToInactive();
    }
    /**
     * This method adds scroll, click and keyup events on document
     * to track the activity.
     */


    toggleActivityEventListeners(true);
    return () => {
      /** Detach document handlers. */
      toggleActivityEventListeners();
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWarningBanner, activity]);
  useEffect(() => {
    activityPrevValue.current = activity;
  }, [activity]);
  useImperativeHandle(ref, () => ({
    stopWriaCookieDataPolling,
    syncCookieData,
    lastPing,
    activity,
    activeElementPriorToBannerDisplay
  }));
  const {
    continueButtonLabel,
    bannerInstructionAnnoucement,
    warningBannerMessage
  } = componentStrings;
  return !showWarningBanner ? null : _jsxs("div", _objectSpread(_objectSpread({
    css: cssRoot,
    ref: ref
  }, otherProps), {}, {
    children: [_jsx("div", {
      css: cssMask
    }), _jsx(MessageBar, {
      zIndex: zIndex,
      open: true,
      className: "pnr-activity-monitor__banner",
      theme: "warning",
      messageBarStyles: cssMessagebarStyles,
      "aria-labelledby": `pnr-activity-monitor-body ${continueActivityID}`,
      closeButton: _jsx(Button, {
        id: continueActivityID,
        "aria-describedby": "pnr-activity-monitor-body",
        onClick: handleContinueActivityClick,
        kind: "standard",
        slim: true,
        children: continueButtonLabel
      }),
      icon: _jsx(IconWarning, {
        className: "pnr-activity-monitor__icon",
        decorative: true,
        theme: "impairment"
      }),
      children: _jsxs("div", {
        // eslint-disable-line jsx-a11y/no-static-element-interactions
        id: "pnr-activity-monitor-body",
        role: "text" // eslint-disable-line jsx-a11y/aria-role
        ,
        css: cssMessageContent,
        ref: messageContainerRef,
        children: [_jsx("span", {
          children: warningBannerMessage
        }), _jsx("span", {
          className: "wf-u-visually-hidden",
          children: bannerInstructionAnnoucement
        })]
      })
    })]
  }));
});
ActivityMonitor.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * A key in the WRIA cookie that is used for activity tracking.
   */
  cookiePrefix: PropTypes.string,

  /**
   * Override for strings used within the component.
   */
  customStrings: PropTypes.shape({
    /**
     * The label for the button in the warning banner that will trigger user
     * activity, and cause the warning banner to disappear.
     */
    continueButtonLabel: PropTypes.string,

    /**
     * Additional Instructions to be read by screen readers on how to continue
     * tracking activity once the warning banner appears.
     * Default: 'To continue your session, press any key or tap, click,
     * or scroll screen.'
     */
    bannerInstructionAnnoucement: PropTypes.string,

    /**
     * The template to display on warning banner.
     */
    warningBannerMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
  }),

  /**
   * The bottom-most domain shared by the applications that are being monitored.
   * For example, if all of your applications are hosted on `foo.wellsfargo.com`
   * and `bar.wellsfargo.com`, then use `.wellsfargo.com`. If your applications
   * are hosted on `baz.subdomain.wellsfargo.com` and `buz.subdomain.wellsfargo.com`,
   * then use `.subdomain.wellsfargo.com`.
   */
  domain: PropTypes.string,

  /**
   * `true` to forcefully trigger inactivity and signal all other pages within
   * the domain that include the ActivityMonitor to call their respective
   * `onInactive` prop. Typically this will only be set to `true` when the user
   * does something to stop activity (e.g. the user clicks on a Sign off button).
   */
  inactive: PropTypes.bool,

  /**
   * A callback to execute when the ActivityMonitor has timed out.
   * It is applicable when `signOffUrl` prop is not configured.
   */
  onInactive: PropTypes.func,

  /**
   * A callback to execute when the warning banner is displayed.
   */
  onWarning: PropTypes.func,

  /**
   * Any protected URL that can be accessed only if the user is authenticated.
   * It will be used to ping and keep the application session alive on the server side,
   * because pinging a protected asset always extends the WAS session.
   * The ping will occur at most every 30 seconds if activity (click, scroll, etc.) is
   * detected in the browser.
   * It accepts a string or a function that returns the URL to be pinged.
   * For cache busting purposes, it is recommended to use a function
   * returning a string URL appended with random numbers.
   * Required when `type` prop is set to `root` or `child`.
   */
  pingUrl: requiredIf(PropTypes.oneOfType([PropTypes.string, PropTypes.func]), (props, propName) => props.type !== LOGON && !props[propName], "if `type` prop is set to `root` or `child`"),

  /**
   * The Absolute URL to which the page gets redirected to.
   *
   */
  signOffUrl: PropTypes.string,

  /**
   * The application window's type:
   *
   * <ul>
   *  <li>
   *    `logon`: The signon page should have the type set to "logon".
   *  </li>
   *  <li>
   *    `root`: root page of the application should the type set to "root".
   *  </li>
   *  <li>
   *    `child`: Any child window should have the type set to "child".
   *  </li>
   *  <li>
   *    `mfe`: Any mfe application which is hosted in mfe container should have the type set to "mfe".
   *    Below are the characteristics of type `mfe`.
   *    - does not detect activity on the page.
   *    - reads cookie and based on the cookie value, use the pingUrl prop and pings
   *      it if needed.
   *	  - does not show warning banner
   *    - does not redirect to logoff url, even if provided.
   *  </li>
   *
   * </ul>
   *
   */
  type: PropTypes.oneOf([LOGON, ROOT, CHILD, MFE]),

  /**
   * Time, in milliseconds, to wait after user activity before sending a request
   * to the `pingUrl`.
   */
  waitTimeToPingServerOnActivity: PropTypes.number,

  /**
   * Time, in milliseconds, to wait to display the warning banner.
   */
  waitTimetoDisplayWarningBanner: PropTypes.number,

  /**
   * Time, in milliseconds, to show the warning banner.
   */
  warningBannerDuration: PropTypes.number,

  /**
   * The `zIndex` to be set on the warning banner in relation with other
   * elements on the page.
   */
  zIndex: PropTypes.number
} : {};
ActivityMonitor.defaultProps = {
  cookiePrefix: "ceo",
  customStrings: null,
  domain: ".wellsfargo.com",
  inactive: false,
  onInactive: null,
  onWarning: null,
  pingUrl: null,
  signOffUrl: null,
  type: ROOT,

  /** 13 minutes and 30 seconds */
  waitTimetoDisplayWarningBanner: 810000,

  /** 30 seconds */
  waitTimeToPingServerOnActivity: 30000,

  /** Expire after 1 minute. */
  warningBannerDuration: 60000,
  zIndex: ZINDEX.ACTIVITY_BANNER
};
/**
 * Typically this is only necessary when your login page is also part of your
 * single-page app. In this case, you will want to invoke this method immediately
 * after the user authenticates.
 *
 * Usage:
 * `<Button onClick={()=> {ActivityMonitor.resetTrackingActivity()}}>Login</Button>`
 *
 * @public
 */

ActivityMonitor.resetTrackingActivity = (domain, type, cookiePrefix) => {
  /**
   * Reset the cookies only for type `logon`.
   */
  if (type !== LOGON) {
    return;
  } // Clear all the cookies.


  setCookieData(cookiePrefix, FLAG_KILL_ACTIVITY, "", domain);
  setCookieData(cookiePrefix, FLAG_ACTIVITY_TIMESTAMP, getCurrentTime(), domain);
  setCookieData(cookiePrefix, FLAG_LOGON_URL, "", domain);
};
/**
 * This static method is only necessary when you have to programmatically simulate the activity.
 * This is not a usual case and should be avoided. Please check with the Pioneer WFRIA team if you
 * feel you need to use this method.
 *
 * @public
 */


ActivityMonitor.simulateActivity = () => {
  document.body.click();
};

ActivityMonitor.displayName = "ActivityMonitor";
export default ActivityMonitor;